import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { ProjectAlignment } from '../../../../shared/helpers/alignment-stationing';
import {
  activeAlignmentIndex, alignmentEmphasis, alignmentLabelled, calloutPlacement, nextAutoIndex, closestAlignment, deconflict, isEmptyMapClick, labelWidthPx, LonLat, pointerStationReadout, ScreenBox, stationingMarks,
  StationPick, stationPick, stationPickLine,
} from '../../../../shared/helpers/stationing-layer';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';

/** The place dialog's alignment blue (By stations), lighter here so records and pins stay on top */
const LINE = [16, 54, 214, 0.55];
const TICK = [16, 54, 214, 0.75];
/** §3c: the active alignment, bold in full blue; the others at this share of their opacity */
const ACTIVE_LINE = [16, 54, 214, 1];
const FADED = 0.4;
const LABEL_INK = [3, 19, 86, 1];
const NAME_INK = [16, 54, 214, 1];
const CHIP_FILL = [255, 255, 255, 1];
const LABEL_FONT = { family: 'Montserrat', size: 9, weight: 'bold' as const };
/** The chip's hairline (Gray 2) */
const CHIP_BORDER = [194, 200, 220, 1];
const NAME_FONT = { family: 'Montserrat', size: 10, weight: 'bold' as const };
/** A label's rough height on screen, for keeping labels apart (its width: labelWidthPx) */
const LABEL_HEIGHT_PX = 13;

const line = (points: LonLat[]) => new Polyline({ paths: [points.map(p => [p.lon, p.lat])], spatialReference: { wkid: 4326 } });

/**
 * 7.2: Stationing on the project map, a client-side layer under everything else: each alignment's line, its station
 * ticks and labels (zoom-aware and culled to the screen, redrawn when the map settles, labels never overlapping), the
 * names along the lines from mid zoom out, and the pointer's station readout within 300 ft of an alignment.
 * The labels (names and stations) are a layer of their own on top, so a site or design line drawn along the
 * alignment never covers them, and shown on the full map only.
 */
export class StationingMapLayer {
  readonly layer = new GraphicsLayer({ id: 'stationing-layer', title: 'Stationing', listMode: 'hide' });
  readonly labelLayer = new GraphicsLayer({ id: 'stationing-label-layer', title: 'Stationing labels', listMode: 'hide' });
  /** The half-height project map is too small for the chips: it keeps the lines and ticks */
  private labelsShown = true;
  private alignments: ProjectAlignment[] = [];
  private handles: { remove(): void }[] = [];
  private frame: number | null = null;
  /** The readout chip, in the map's bottom-left corner (the view's UI, so it follows full screen) */
  readonly readout: HTMLDivElement = document.createElement('div');
  /** Spec §3: the clicked point's marker (above everything) and its callout, anchored to the point */
  readonly pickLayer = new GraphicsLayer({ id: 'stationing-pick-layer', title: 'Station pick', listMode: 'hide' });
  readonly callout: HTMLDivElement = document.createElement('div');
  /** The callout's short pointer to its dot */
  private readonly pointer: HTMLDivElement = document.createElement('div');
  private picked: { at: LonLat; pick: StationPick } | null = null;
  /** §3c: the locked alignment's name, or null for Automatic (the nearest to the last click or pointer) */
  private locked: string | null = null;
  private autoIndex = -1;
  /** The host has a record popup or feature panel open with a station line: the pointer must not change it (§3c) */
  holdsLine: () => boolean = () => false;
  /** Told when the active alignment changes (the host's chip) */
  onActiveChange: (name: string | null, locked: boolean) => void = () => undefined;
  private readonly onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && this.picked) this.dismiss(); };

  constructor(private readonly view: __esri.MapView, private readonly onReadout: (text: string | null) => void = () => undefined) {
    // Bottom of the stack (over the basemap): records, pins and design layers stay dominant
    view.map.add(this.layer, 0);
    view.map.add(this.labelLayer);
    // Inline: made here, outside any component's styles. The map controls' white chip and elevation.
    Object.assign(this.readout.style, {
      display: 'none', padding: '6px 12px', borderRadius: '16px', background: '#FFFFFF', border: '1px solid #E9ECF4', color: '#060B1E',
      font: "600 14px/20px 'Montserrat', sans-serif", whiteSpace: 'nowrap', pointerEvents: 'none', boxShadow: '2px 4px 12px 0 rgba(195, 199, 213, 1)',
    });
    this.readout.setAttribute('data-testid', 'map-station-readout');
    this.readout.setAttribute('role', 'status');
    view.ui?.add(this.readout, 'bottom-left');
    view.map.add(this.pickLayer);
    this.buildCallout();
    view.container?.appendChild(this.callout);
    document.addEventListener('keydown', this.onKey);
    this.handles.push(
      reactiveUtils.watch(() => [view.extent, view.size], () => this.placeCallout()),
      reactiveUtils.when(() => view.stationary, () => this.draw()),
      view.on('pointer-move', event => this.track(event.x, event.y)),
      view.on('pointer-leave', () => this.show(null)),
      // Records, sites and design layers load after the map does (and are re-added when toggled): labels stay on top
      view.map.layers.on('change', () => this.placeLabelLayer()),
    );
    this.placeLabelLayer();
  }

  /** On top of the map's layers (only the clicked point's marker above): the site lines and design layers are drawn
   *  along the alignment and would cover its name chip; layers load and get re-added after the map opens */
  private placeLabelLayer(): void {
    const layers = this.view.map?.layers;
    if (!layers) return;
    const items = layers.toArray();
    const want = items.length - 1 - (items.includes(this.pickLayer) ? 1 : 0);
    const at = items.indexOf(this.labelLayer);
    if (at !== -1 && at !== want) this.view.map.reorder(this.labelLayer, want);
  }

  get visible(): boolean {
    return this.layer.visible;
  }

  /** A click on the empty map gets a station callout (Stationing on, the project has alignments) */
  get answersClicks(): boolean {
    return this.layer.visible && this.alignments.length > 0;
  }

  setAlignments(alignments: ProjectAlignment[]): void {
    this.alignments = alignments;
    this.autoIndex = -1;
    this.draw();
    this.notifyActive();
  }

  /** Labels on the full map, none on the half-height map (the lines and ticks stay) */
  setLabelsShown(shown: boolean): void {
    if (this.labelsShown === shown) return;
    this.labelsShown = shown;
    this.draw();
  }

  setVisible(visible: boolean): void {
    this.layer.visible = visible;
    this.labelLayer.visible = visible;
    if (!visible) {
      this.show(null);
      this.dismiss();
    }
    this.draw();
  }

  destroy(): void {
    this.handles.forEach(h => h.remove());
    this.handles = [];
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.view.ui?.remove(this.readout);
    document.removeEventListener('keydown', this.onKey);
    this.callout.remove();
    this.view.map?.remove(this.pickLayer);
    this.pickLayer.destroy();
    this.view.map?.remove(this.labelLayer);
    this.labelLayer.destroy();
    this.view.map?.remove(this.layer);
    this.layer.destroy();
  }

  /** Redraws for the extent and zoom the map settled on */
  draw(): void {
    const view = this.view;
    this.layer.removeAll();
    this.labelLayer.removeAll();
    if (!this.layer.visible || !this.alignments.length || !view?.extent) return;
    const extent = view.extent.clone().expand(1.2);
    const geo = extent.spatialReference?.isWebMercator
      ? (webMercatorBox(extent) as { xmin: number; ymin: number; xmax: number; ymax: number })
      : { xmin: extent.xmin, ymin: extent.ymin, xmax: extent.xmax, ymax: extent.ymax };
    const lat = view.center?.latitude ?? 0;
    const mpp = view.resolution * Math.cos((lat * Math.PI) / 180);
    const marks = stationingMarks(this.alignments, mpp, geo);

    // §3c: the active alignment bold in full blue, the others faded (their marks too); one alignment is never faded
    const active = this.activeIndex;
    const emphasis = (index: number) => alignmentEmphasis(index, active, this.alignments.length);
    const fade = (rgba: number[], index: number) => (emphasis(index) === 'faded' ? [rgba[0], rgba[1], rgba[2], rgba[3] * FADED] : rgba);
    this.alignments.forEach((a, index) => {
      const bold = emphasis(index) === 'active';
      this.layer.add(new Graphic({
        geometry: line(a.alignment.coords.map(([lon, latitude]) => ({ lon, lat: latitude }))),
        symbol: new SimpleLineSymbol({ color: bold ? ACTIVE_LINE : fade(LINE, index), width: bold ? 3 : 1.5 }),
      }));
    });
    for (const t of marks.ticks) {
      this.layer.add(new Graphic({ geometry: line([t.left, t.right]), symbol: new SimpleLineSymbol({ color: fade(TICK, t.alignment), width: t.major ? 1.5 : 1 }) }));
    }
    if (!this.labelsShown) return;
    // Names first (they matter most when several alignments run side by side), then stations in order
    // Only the active alignment is labelled (the others are a faded line and ticks); a lone alignment always is
    const labelled = (index: number) => alignmentLabelled(index, active, this.alignments.length);
    const texts = [
      ...marks.names.filter(n => labelled(n.alignment))
        .map(n => ({ at: n.at, text: n.text, name: true, angle: n.angle, align: 'center' as const, alignment: n.alignment })),
      ...marks.labels.filter(l => labelled(l.alignment))
        .map(l => ({ at: l.at, text: l.text, name: false, angle: 0, align: l.align, alignment: l.alignment })),
    ];
    const boxes: ScreenBox[] = texts.map(t => {
      const p = view.toScreen(new Point({ longitude: t.at.lon, latitude: t.at.lat }));
      const width = labelWidthPx(t.text);
      const x = p?.x ?? 0;
      // A name is centred on its point; a station runs away from the line from its point
      const left = t.align === 'center' ? x - width / 2 : t.align === 'left' ? x : x - width;
      return { x: left, y: (p?.y ?? 0) - LABEL_HEIGHT_PX / 2, width, height: LABEL_HEIGHT_PX };
    });
    // Labels stay out from under the map's floating panels and buttons (and across all alignments, names first)
    const keep = deconflict(boxes, 2, box => this.coveredOnScreen(box));
    texts.forEach((t, i) => {
      if (!keep[i]) return;
      this.labelLayer.add(new Graphic({
        geometry: new Point({ longitude: t.at.lon, latitude: t.at.lat }),
        // The mobile chip: small bold dark-blue text on a white rounded box with a hairline (ArcGIS draws the box tight)
        symbol: new TextSymbol({
          text: t.text, color: fade(t.name ? NAME_INK : LABEL_INK, t.alignment), backgroundColor: fade(CHIP_FILL, t.alignment),
          borderLineColor: fade(CHIP_BORDER, t.alignment), borderLineSize: 0.75, angle: t.angle, horizontalAlignment: t.align,
          font: t.name ? NAME_FONT : LABEL_FONT,
        }),
      }));
    });
  }

  /**
   * A label box is covered when, at its corners or centre, something other than the map is on top: the project card,
   * the header, the zoom stack, the View panel (elementFromPoint skips the click-through readout chip and callout)
   */
  private coveredOnScreen(box: ScreenBox): boolean {
    const container = this.view.container;
    const surface = (this.view as unknown as { surface?: HTMLElement }).surface ?? container;
    if (!container || typeof document.elementFromPoint !== 'function') return false;
    const r = container.getBoundingClientRect();
    const points = [[box.x, box.y], [box.x + box.width, box.y], [box.x, box.y + box.height], [box.x + box.width, box.y + box.height],
      [box.x + box.width / 2, box.y + box.height / 2]];
    return points.some(([x, y]) => {
      if (x < 0 || y < 0 || x > r.width || y > r.height) return true;
      const top = document.elementFromPoint(r.left + x, r.top + y);
      // Menus and their backdrops come and go (the alignment chip's menu is open when it redraws): never a reason to hide
      return !!top && !surface.contains(top) && top !== this.callout && !this.callout.contains(top) && !top.closest('.cdk-overlay-container');
    });
  }

  /** A record's popup or a feature's details opened at this point: in Automatic, its nearest becomes active (§3c) */
  focusPoint(point: { latitude?: number | null; longitude?: number | null } | null | undefined): void {
    if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return;
    this.followPoint({ lat: point.latitude, lon: point.longitude }, 'click');
  }

  /** §3c: the alignment the map reads stations on (locked, or in Automatic the nearest to the last click or pointer) */
  get activeIndex(): number {
    return activeAlignmentIndex(this.alignments, this.locked, null, this.autoIndex);
  }

  get activeName(): string | null {
    return this.alignments[this.activeIndex]?.name ?? null;
  }

  get alignmentNames(): string[] {
    return this.alignments.map(a => a.name).filter(Boolean);
  }

  get isLocked(): boolean {
    return !!this.locked && this.alignments.some(a => a.name === this.locked);
  }

  /** Locks the active alignment to a name, or (null) back to Automatic */
  setLocked(name: string | null): void {
    this.locked = name;
    this.draw();
    this.repick();
    this.notifyActive();
  }

  /** In Automatic, the nearest alignment to this point becomes active (redrawn only when it changes) */
  private followPoint(at: LonLat, source: 'click' | 'pointer'): void {
    if (this.isLocked || this.alignments.length < 2) return;
    const nearest = closestAlignment(this.alignments, at)?.index ?? -1;
    const next = nextAutoIndex(this.autoIndex, nearest, source, !!this.picked || this.holdsLine());
    if (next === this.autoIndex) return;
    this.autoIndex = next;
    this.draw();
    this.repick();
    this.notifyActive();
  }

  /** The open callout follows a change of active alignment: same dot, its line read again on the new one */
  private repick(): void {
    const picked = this.picked;
    if (!picked) return;
    const pick = stationPick(this.alignments, picked.at, this.activeName);
    if (!pick) {
      this.dismiss();
      return;
    }
    this.picked = { at: picked.at, pick };
    this.renderCallout();
  }

  private notifyActive(): void {
    this.onActiveChange(this.activeName, this.isLocked);
  }

  /** Spec §3a: the line a record's or feature's popup gains for the clicked point; null with Stationing off */
  lineAt(point: { latitude?: number | null; longitude?: number | null } | null | undefined): string | null {
    if (!this.layer.visible || !this.alignments.length || !point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
    // The active alignment (locked, or Automatic's current nearest), so an open popup's line follows a change of it
    return stationPickLine(this.alignments, { lat: point.latitude, lon: point.longitude }, this.activeName);
  }

  get pickedPoint(): { at: LonLat; pick: StationPick } | null {
    return this.picked;
  }

  /**
   * Spec §3: a click on the empty map (Stationing on, the project has alignments) drops a marker with the point's
   * station and offset; a click on a record, pin, cluster or feature leaves it to that popup and clears the pick.
   * The map's host calls it once a design-layer inspection has left the click to Stationing (§3a).
   */
  async onClick(event: { x: number; y: number; mapPoint?: __esri.Point | null }): Promise<void> {
    if (!this.answersClicks) return;
    const hits = await this.view.hitTest(event);
    if (!isEmptyMapClick(hits?.results ?? [], [this.layer, this.pickLayer])) {
      this.dismiss();
      return;
    }
    const p = event.mapPoint ?? this.view.toMap(event);
    if (!p) return;
    const at = { lat: p.latitude, lon: p.longitude };
    // The pick first, so Automatic's change to this point's nearest re-reads it on that alignment
    this.picked = null;
    this.followPoint(at, 'click');
    const pick = stationPick(this.alignments, at, this.activeName);
    if (!pick) return;
    this.picked = { at, pick };
    this.pickLayer.removeAll();
    this.pickLayer.add(new Graphic({
      geometry: new Point({ longitude: at.lon, latitude: at.lat }),
      symbol: new SimpleMarkerSymbol({ style: 'circle', size: 9, color: [16, 54, 214, 1], outline: { color: [255, 255, 255, 1], width: 2 } }),
    }));
    this.renderCallout();
  }

  dismiss(): void {
    this.picked = null;
    this.pickLayer.removeAll();
    this.callout.style.display = 'none';
  }

  /** The white chip: the lines, then Copy and ✕ (inline styles: made here, outside any component's styles) */
  private buildCallout(): void {
    Object.assign(this.callout.style, {
      position: 'absolute', display: 'none', zIndex: '2', padding: '8px 10px 8px 12px',
      background: '#FFFFFF', border: '1px solid #E9ECF4', borderRadius: '4px', color: '#060B1E', font: "500 12px/18px 'Montserrat', sans-serif",
      whiteSpace: 'nowrap', boxShadow: '2px 4px 12px 0 rgba(195, 199, 213, 1)',
    });
    this.callout.setAttribute('data-testid', 'map-station-pick');
    Object.assign(this.pointer.style, { position: 'absolute', width: '0', height: '0', borderStyle: 'solid', marginLeft: '-6px' });
    this.pointer.setAttribute('data-testid', 'map-station-pick-pointer');
    this.callout.setAttribute('role', 'dialog');
    this.callout.setAttribute('aria-label', 'Station at this point');
  }

  private renderCallout(): void {
    const picked = this.picked;
    if (!picked) return;
    const button = (label: string, testId: string, aria: string, onClick: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.setAttribute('data-testid', testId);
      b.setAttribute('aria-label', aria);
      Object.assign(b.style, { border: '0', background: 'transparent', padding: '0 0 0 8px', cursor: 'pointer', color: '#1036D6', font: "600 12px/18px 'Montserrat', sans-serif" });
      b.addEventListener('click', e => { e.stopPropagation(); onClick(); });
      return b;
    };
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'flex-start', gap: '4px' });
    const text = document.createElement('div');
    picked.pick.lines.forEach((line, i) => {
      const el = document.createElement('div');
      el.textContent = line;
      el.setAttribute('data-testid', `map-station-pick-line-${i + 1}`);
      // One short line (spec §3c)
      el.style.fontWeight = '600';
      el.style.color = '#060B1E';
      text.appendChild(el);
    });
    row.appendChild(text);
    if (picked.pick.copy) {
      const copy = picked.pick.copy;
      const b = button('Copy', 'map-station-pick-copy', `Copy ${copy}`, () => {
        void copyText(copy).then(done => { if (done) b.textContent = 'Copied'; });
      });
      row.appendChild(b);
    }
    const close = button('✕', 'map-station-pick-close', 'Close', () => this.dismiss());
    close.style.color = '#60698B';
    row.appendChild(close);
    this.callout.replaceChildren(row, this.pointer);
    this.callout.style.display = 'block';
    this.placeCallout();
  }

  /**
   * Keeps the callout on its point while the map pans and zooms: above the dot, or below it when there is no room above,
   * never over it (spec §3b), clamped on screen, with its pointer on the dot
   */
  private placeCallout(): void {
    const picked = this.picked;
    if (!picked) return;
    const p = this.view.toScreen(new Point({ longitude: picked.at.lon, latitude: picked.at.lat }));
    if (!p) return;
    const box = calloutPlacement(p, { width: this.callout.offsetWidth, height: this.callout.offsetHeight },
      { width: this.view.width ?? 0, height: this.view.height ?? 0 });
    this.callout.style.left = `${box.left}px`;
    this.callout.style.top = `${box.top}px`;
    this.pointer.style.left = `${box.pointerLeft}px`;
    Object.assign(this.pointer.style, box.below
      ? { top: '-6px', bottom: '', borderWidth: '0 6px 6px 6px', borderColor: 'transparent transparent #C2C8DC transparent' }
      : { top: '', bottom: '-6px', borderWidth: '6px 6px 0 6px', borderColor: '#C2C8DC transparent transparent transparent' });
  }

  /** The pointer's readout, once a frame */
  private track(x: number, y: number): void {
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.layer.visible || !this.alignments.length) {
        this.show(null);
        return;
      }
      const p = this.view.toMap({ x, y });
      if (!p) {
        this.show(null);
        return;
      }
      const at = { lat: p.latitude, lon: p.longitude };
      this.followPoint(at, 'pointer');
      // The readout reads the nearest to the pointer even while an open callout holds the highlight
      this.show(pointerStationReadout(this.alignments, at, this.locked));
    });
  }

  private show(text: string | null): void {
    if ((this.readout.textContent || null) === text && (this.readout.style.display !== 'none') === !!text) return;
    this.readout.textContent = text ?? '';
    this.readout.style.display = text ? 'block' : 'none';
    this.onReadout(text);
  }
}

/** A Web Mercator extent as a lon / lat box */
function webMercatorBox(extent: __esri.Extent): { xmin: number; ymin: number; xmax: number; ymax: number } {
  const lon = (x: number) => (x / 6378137) * (180 / Math.PI);
  const lat = (y: number) => (Math.atan(Math.exp(y / 6378137)) * 2 - Math.PI / 2) * (180 / Math.PI);
  return { xmin: lon(extent.xmin), ymin: lat(extent.ymin), xmax: lon(extent.xmax), ymax: lat(extent.ymax) };
}

/** The clipboard, or (outside a secure page, where there is no clipboard API) the old copy command */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall back below */ }
  const area = document.createElement('textarea');
  area.value = text;
  Object.assign(area.style, { position: 'fixed', opacity: '0', pointerEvents: 'none' });
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}
